import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useStudent } from '../context/StudentContext';
import {
  buildStudentSidebarTree,
  collectExpandedSidebarNodeIds,
} from '../utils/buildStudentSidebarTree';
import { isStudentSidebarTabActive } from '../utils/studentSidebarNavigation';
import { listOrderedCourseTabs } from '../utils/normalizeCourseShape';

const sidebarTreeNodeShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(PropTypes.object),
});

function isSidebarTreeNodeActive(node, pathname, search, hints) {
  const current = `${pathname}${search}`;
  if (current === node.href) {
    return true;
  }
  if (node.kind === 'tab') {
    return isStudentSidebarTabActive(pathname, search, node.label, hints);
  }
  return false;
}

function SidebarTreeItem({
  node,
  depth,
  expandedIds,
  onToggleExpand,
  pathname,
  search,
  hints,
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isActive = isSidebarTreeNodeActive(node, pathname, search, hints);

  return (
    <li className={`side-sidebar-tree-item side-sidebar-tree-item--depth-${depth}`}>
      <div className="side-sidebar-tree-row">
        {hasChildren ? (
          <button
            type="button"
            className="side-sidebar-tree-toggle"
            aria-label={isExpanded ? 'Contraer sección' : 'Expandir sección'}
            aria-expanded={isExpanded}
            onClick={() => onToggleExpand(node.id)}
          >
            {isExpanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="side-sidebar-tree-toggle-spacer" aria-hidden="true" />
        )}
        <Link
          to={node.href}
          className={`side-sidebar-button side-sidebar-tree-link${isActive ? ' active' : ''}`}
        >
          {node.label}
        </Link>
      </div>
      {hasChildren && isExpanded ? (
        <ul className="side-sidebar-tree-children">
          {node.children.map((child) => (
            <SidebarTreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              pathname={pathname}
              search={search}
              hints={hints}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

SidebarTreeItem.propTypes = {
  node: sidebarTreeNodeShape.isRequired,
  depth: PropTypes.number.isRequired,
  expandedIds: PropTypes.instanceOf(Set).isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  hints: PropTypes.shape({
    contenidoTabs: PropTypes.arrayOf(PropTypes.string),
    testBlocks: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

function StudentCourseSidebarNav() {
  const location = useLocation();
  const {
    activeCourse,
    activeCourseTabHints,
    activeCourseNavigation,
    messagingEnabled,
  } = useStudent();
  const [manualExpandedIds, setManualExpandedIds] = useState([]);

  const tree = useMemo(
    () => buildStudentSidebarTree({
      pestanasCurso: listOrderedCourseTabs(
        activeCourseNavigation?.pestanasCurso?.length
          ? activeCourseNavigation.pestanasCurso
          : activeCourse?.pestanasCurso,
      ),
      contenido: activeCourseNavigation?.contenido ?? {},
      testEstructura: activeCourseNavigation?.testEstructura ?? [],
      courseTests: activeCourse?.tests ?? activeCourseNavigation?.tests,
      hints: activeCourseTabHints,
    }),
    [activeCourse?.pestanasCurso, activeCourse?.tests, activeCourseNavigation, activeCourseTabHints],
  );

  const routeExpandedIds = useMemo(
    () => collectExpandedSidebarNodeIds(tree, location),
    [tree, location],
  );

  const expandedIds = useMemo(() => {
    return new Set([...routeExpandedIds, ...manualExpandedIds]);
  }, [routeExpandedIds, manualExpandedIds]);

  const onToggleExpand = (nodeId) => {
    setManualExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return Array.from(next);
    });
  };

  if (!activeCourse || tree.length === 0) {
    return null;
  }

  return (
    <>
      {tree.map((tabNode) => {
        const isMessagingTab = tabNode.href.includes('mensajeria');
        if (isMessagingTab && !messagingEnabled) {
          return null;
        }
        return (
          <SidebarTreeItem
            key={tabNode.id}
            node={tabNode}
            depth={0}
            expandedIds={expandedIds}
            onToggleExpand={onToggleExpand}
            pathname={location.pathname}
            search={location.search}
            hints={activeCourseTabHints}
          />
        );
      })}
    </>
  );
}

export default StudentCourseSidebarNav;
